import React, { useState, useReducer } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  makeStyles,
  Paper,
  Button,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  IconButton,
} from "@material-ui/core";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { useForgotPasswordStore } from "../../store/forgotPassword";
import SnackBar from "../../pages/admin/layout/components/Snackback";
import axios from "../../services/api";

import passwordReducer, {
  PasswordsProps,
} from "../../components/ChangePassword/reducer/password.reducer";
import { useHistory } from "react-router-dom";
import {
  ButtonColorCurrentApp,
  ColorDotsByCurrentApp,
  GradientBackgroundImageByCurrentApp,
} from "../../utils/get-app-env";

const useStyles = makeStyles((theme) => ({
  root: {
    background: GradientBackgroundImageByCurrentApp(),
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
  },
  container: {
    width: "fit-content",
    color: "#fff",
  },
  paper: {
    padding: theme.spacing(4),
    maxWidth: 520,
    width: "100%",
    backgroundColor: "#fff",
    borderRadius: theme.spacing(2),
    color: "#1e1e1e",
  },
  stepIndicator: {
    display: "flex",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    "& > div": {
      flex: 1,
      height: 8,
      borderRadius: 4,
      marginRight: theme.spacing(1),
      backgroundColor: "#333",
      "&:nth-child(1)": {
        backgroundColor: ColorDotsByCurrentApp(),
      },
      "&:nth-child(2)": {
        backgroundColor: ColorDotsByCurrentApp(),
      },
      "&:nth-child(3)": {
        backgroundColor: ColorDotsByCurrentApp(),
      },
    },
  },
  codeContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  codeInput: {
    width: 48,
    height: 48,
    fontSize: "1.5rem",
    textAlign: "center",
    backgroundColor: "#121212",
    border: "1px solid #fff",
    borderRadius: 4,
    color: "#fff",
  },
  button: {
    marginBottom: theme.spacing(3),
    backgroundColor: "#ccc",
    color: "#fff",
    fontWeight: 600,
    transition: "background-color 0.3s ease",
    "&:not(:disabled)": {
      backgroundColor: ButtonColorCurrentApp(),
    },
    "&:not(:disabled):hover": {
      backgroundColor: ButtonColorCurrentApp(),
    },
  },
  resendBox: {
    border: "1px solid #fff",
    borderRadius: 4,
    padding: theme.spacing(1),
    textAlign: "center",
    color: "#ccc",
  },
  footer: {
    marginTop: theme.spacing(5),
    backgroundColor: "#2c2c2c",
    padding: theme.spacing(2),
    borderRadius: 4,
    fontSize: "0.75rem",
    textAlign: "center",
    color: "#aaa",
    "& a": {
      color: "#90caf9",
      textDecoration: "none",
      marginLeft: 4,
    },
  },
  textField: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    "& input": {
      color: "#000",
    },
    "& label": {
      color: "#aaa",
    },
    "& .MuiInput-underline:before": {
      borderBottomColor: "#555",
    },
  },
  inputRoot: {
    color: "#fff",
    "& .MuiInput-underline:before": {
      borderBottomColor: "#fff", // linha normal
    },
    "& .MuiInput-underline:hover:before": {
      borderBottomColor: "#fff", // linha no hover
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#fff", // linha no focus (FOCO)
    },
  },
  label: {
    color: "#fff",
    "&.Mui-focused": {
      color: "#fff", // label focado
    },
  },
  submitButton: {
    marginBottom: theme.spacing(3),
    backgroundColor: "#ccc",
    color: "#fff",
    fontWeight: 600,
    transition: "background-color 0.3s ease",
    "&:not(:disabled)": {
      backgroundColor: ButtonColorCurrentApp(),
    },
    "&:not(:disabled):hover": {
      backgroundColor: ButtonColorCurrentApp(),
    },
  },
}));

const initialState: PasswordsProps = {
  currentPassword: "",
  newPassword: "",
  confirmNewPassword: "",
  errors: [],
  snackbar: {
    isOpen: false,
    message: "",
    type: "info",
  },
};

const CreateNewPsswordPage: React.FC = () => {
  const classes = useStyles();
  const [passwords, dispatch] = useReducer(passwordReducer, initialState);
  const history = useHistory();

  const { email } = useForgotPasswordStore();

  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState("");

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const isValidPassword = (password: string) => {
    const regex =
      /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/;
    return regex.test(password);
  };

  const handleSubmit = async () => {
    if (!password || !confirmPassword) {
      dispatch({
        type: "SET_SNACKBAR",
        payload: {
          isOpen: true,
          message: "Por favor, preencha todos os campos.",
          type: "error",
        },
      });
      return;
    }

    if (password !== confirmPassword) {
      dispatch({
        type: "SET_SNACKBAR",
        payload: {
          isOpen: true,
          message: "As senhas não coincidem.",
          type: "error",
        },
      });
      return;
    }

    if (!isValidPassword(password)) {
      dispatch({
        type: "SET_SNACKBAR",
        payload: {
          isOpen: true,
          message:
            "A senha deve ter no mínimo 8 caracteres, com letras maiúsculas, números e caracteres especiais.",
          type: "error",
        },
      });
      return;
    }

    try {
      const response = await axios.post("/auth/reset-password", {
        email,
        newPassword: password,
      });

      console.log("Senha atualizada com sucesso:", response.data);
      dispatch({
        type: "SET_SNACKBAR",
        payload: {
          isOpen: true,
          message: "Senha atualizada com sucesso!",
          type: "info",
        },
      });

      history.push("/login");
    } catch (error: any) {
      console.error("Erro ao atualizar senha:", error);

      dispatch({
        type: "SET_SNACKBAR",
        payload: {
          isOpen: true,
          message: "Ocorreu um erro ao atualizar sua senha.",
          type: "error",
        },
      });
    }
  };

  return (
    <>
      <Box className={classes.root}>
        <Container className={classes.container}>
          <Paper elevation={6} className={classes.paper}>
            <Typography variant="h3" gutterBottom>
              Crie uma nova senha
            </Typography>

            <Box className={classes.stepIndicator}>
              <div />
              <div />
              <div />
            </Box>

            <Typography variant="body2" gutterBottom>
              A sua nova senha deve ter no mínimo 8 dígitos com números, letras
              maiúsculas e caracteres especiais. (Exemplo: ./#*)
            </Typography>

            <TextField
              fullWidth
              label="Senha"
              type={showPassword ? "text" : "password"}
              variant="standard"
              className={classes.textField}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                classes: { root: classes.inputRoot },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirme a senha"
              variant="standard"
              type={showConfirmPassword ? "text" : "password"}
              className={classes.textField}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={{
                classes: { root: classes.inputRoot },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleToggleConfirmPasswordVisibility}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              fullWidth
              className={classes.submitButton}
              onClick={handleSubmit}
              disabled={
                !password || !confirmPassword || password !== confirmPassword
              }
            >
              Salvar alterações
            </Button>
          </Paper>
        </Container>
      </Box>
      <SnackBar
        children={<>{passwords.snackbar.message}</>}
        open={passwords.snackbar.isOpen}
        feedbackType={passwords.snackbar.type}
        setOpen={() => {}}
        dispatch={() =>
          dispatch({ type: "SET_SNACKBAR", payload: { isOpen: false } })
        }
      />
    </>
  );
};

export default CreateNewPsswordPage;
